<template>
    <div class="settings-variables">
        <div class="sv-header">
            <h3 class="sv-title">{{ $t('dash.settings.variables.title') }}<BetaBadge></BetaBadge></h3>
            <p class="sv-subtitle">{{ $t('dash.settings.variables.subtitle') }}</p>
        </div>

        <div class="sv-chips">
            <div class="sv-chip" v-for="variable in variablesList" :key="variable.id">
                <span>{{ variable.name }}</span>
                <i class="fa-solid fa-pencil" @click="openEditForm(variable)"></i>
                <i class="fa-solid fa-xmark" @click="onDelete(variable)"></i>
            </div>

            <div class="sv-add-trigger" v-if="!creating" @click="openCreateForm">
                <i class="fa-solid fa-plus"></i>
                {{ $t('dash.settings.variables.add-button') }}
            </div>

            <div class="sv-create-form" v-else>
                <input
                    type="text"
                    v-model="newVariableName"
                    :placeholder="$t('dash.settings.variables.name-placeholder')"
                    @keyup.enter="submitVariable"
                >
                <button class="sv-btn-submit" :disabled="!newVariableName.trim() || submitting" @click="submitVariable">
                    {{ editingVariableId ? $t('dash.settings.variables.save') : $t('dash.settings.variables.create') }}
                </button>
                <button class="sv-btn-cancel" @click="cancelForm">{{ $t('dash.settings.variables.cancel') }}</button>
            </div>
        </div>

        <p class="sv-error" v-if="formError">{{ formError }}</p>

        <div class="sv-table-wrapper" v-if="variablesList.length">
            <div class="sv-table-toolbar">
                <div class="sv-search">
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <input type="text" v-model="searchPage" :placeholder="$t('dash.settings.variables.search-page-placeholder')">
                </div>
            </div>

            <div class="sv-table-scroll">
                <table class="sv-table">
                    <thead>
                        <tr>
                            <th class="sv-page-col sv-sortable" @click="toggleSort('page')">
                                <div class="sv-th-content">
                                    {{ $t('dash.settings.variables.table-page') }}
                                    <i class="fa-solid sv-sort-icon" :class="sortIconClass('page')"></i>
                                </div>
                            </th>
                            <th v-for="variable in variablesList" :key="variable.id" class="sv-sortable">
                                <div class="sv-th-content">
                                    <span @click="toggleSort(variable.id)">
                                        {{ variable.name }}
                                        <i class="fa-solid sv-sort-icon" :class="sortIconClass(variable.id)"></i>
                                    </span>
                                    <Tooltip :text="$t('dash.settings.variables.bulk-apply-tooltip')" position="top">
                                        <i
                                            class="fa-solid fa-clone sv-bulk-icon"
                                            @click.stop="openBulkApply(variable)"
                                        ></i>
                                    </Tooltip>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="page in sortedPages" :key="page.id">
                            <td class="sv-page-col">
                                <div class="sv-page-cell">
                                    <img
                                        v-if="networkIcon(page.network)"
                                        :src="networkIcon(page.network)"
                                        class="sv-network-icon"
                                        :alt="page.network"
                                    >
                                    <span>{{ page.name }}</span>
                                </div>
                            </td>
                            <td v-for="variable in variablesList" :key="variable.id" class="sv-value-cell">
                                <input
                                    type="text"
                                    class="sv-value-input"
                                    :placeholder="$t('dash.settings.variables.value-placeholder')"
                                    :value="getValue(variable.id, page.id)"
                                    @blur="onValueBlur(variable.id, page.id, $event)"
                                >
                                <Tooltip
                                    v-if="getValue(variable.id, page.id)"
                                    :text="$t('dash.settings.variables.bulk-apply-tooltip')"
                                    position="top"
                                    classCustom="sv-value-bulk-tooltip"
                                >
                                    <i
                                        class="fa-solid fa-clone sv-bulk-icon sv-value-bulk-icon"
                                        @click="openBulkApply(variable, { value: getValue(variable.id, page.id), defaultPageIds: [page.id] })"
                                    ></i>
                                </Tooltip>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p class="sv-empty" v-if="!sortedPages.length">{{ $t('dash.settings.variables.no-page-result') }}</p>
            </div>
        </div>

        <p class="sv-empty" v-else>{{ $t('dash.settings.variables.no-variables') }}</p>

        <BulkApplyVariableValue
            v-if="bulkApply"
            :variable="bulkApply.variable"
            :all-pages="allPages"
            :initial-value="bulkApply.value"
            :initial-selected-page-ids="bulkApply.defaultPageIds"
            @close="bulkApply = null"
        />
    </div>
</template>

<script>
import VariablesMixin from '../../mixins/Variables.vue'
import NotifMixin from '../../mixins/Notif.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import BetaBadge from '../utils/BetaBadge.vue'
import BulkApplyVariableValue from '../variables/BulkApplyVariableValue.vue'

export default {
    name: 'settings-variables',

    components: { Tooltip, BulkApplyVariableValue, BetaBadge },

    mixins: [VariablesMixin, NotifMixin],

    props: {
        allPages: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            creating: false,
            editingVariableId: null,
            newVariableName: '',
            submitting: false,
            formError: null,
            searchPage: '',
            sortColumn: null, // 'page' or a variable id
            sortDirection: 'asc',
            bulkApply: null // { variable, value, defaultPageIds } when the bulk-apply modal is open
        }
    },

    computed: {
        variablesList() {
            return this.$store.getters['variables/getVariablesList']
        },

        values() {
            return this.$store.getters['variables/getValues']
        },

        filteredPages() {
            const q = this.searchPage.trim().toLowerCase()
            if (!q) return this.allPages
            return this.allPages.filter(page => page.name && page.name.toLowerCase().includes(q))
        },

        sortedPages() {
            if (!this.sortColumn) return this.filteredPages

            const dir = this.sortDirection === 'desc' ? -1 : 1
            const getSortValue = (page) => this.sortColumn === 'page'
                ? (page.name || '')
                : this.getValue(this.sortColumn, page.id)

            return [...this.filteredPages].sort((a, b) => {
                const valueA = getSortValue(a)
                const valueB = getSortValue(b)

                // les valeurs vides restent toujours en bas, quel que soit le sens du tri
                if (!valueA && valueB) return 1
                if (valueA && !valueB) return -1

                return valueA.localeCompare(valueB, undefined, { sensitivity: 'base' }) * dir
            })
        }
    },

    mounted() {
        this.$store.dispatch('variables/fetchVariables')
    },

    methods: {
        networkIcon(network) {
            try {
                return require(`../../../assets/img/iconsNetwork/ico-${network}-2x.png`)
            } catch (e) {
                return null
            }
        },

        getValue(variableId, pageId) {
            return (this.values[variableId] && this.values[variableId][pageId]) || ''
        },

        toggleSort(column) {
            if (this.sortColumn === column) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = column
                this.sortDirection = 'asc'
            }
        },

        sortIconClass(column) {
            if (this.sortColumn !== column) return 'fa-sort'
            return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
        },

        // Icône du header de colonne : pas de valeur pré-remplie, toutes les pages
        // cochées par défaut (cf. BulkApplyVariableValue). Icône sur une cellule
        // déjà renseignée : valeur de la cellule pré-remplie, seule la page
        // d'origine est cochée par défaut.
        openBulkApply(variable, { value = '', defaultPageIds = null } = {}) {
            this.bulkApply = { variable, value, defaultPageIds }
        },

        async onValueBlur(variableId, pageId, event) {
            const value = event.target.value
            if (this.getValue(variableId, pageId) === value) return

            const res = await this.saveVariableValue(variableId, pageId, value)
            if (!res || !res.body || !res.body.success) {
                this.notify({ text: 'dash.settings.variables.value-save-failed', color: 'danger' })
            }

            await this.$store.dispatch('variables/fetchVariables')
        },

        openCreateForm() {
            this.editingVariableId = null
            this.newVariableName = ''
            this.formError = null
            this.creating = true
        },

        openEditForm(variable) {
            this.editingVariableId = variable.id
            this.newVariableName = variable.name
            this.formError = null
            this.creating = true
        },

        cancelForm() {
            this.creating = false
            this.editingVariableId = null
            this.newVariableName = ''
            this.formError = null
        },

        async onDelete(variable) {
            if (!confirm(this.$t('dash.settings.variables.delete-confirm'))) return

            await this.deleteVariable(variable.id)
            await this.$store.dispatch('variables/fetchVariables')
        },

        async submitVariable() {
            const name = this.newVariableName.trim()
            if (!name) return

            this.submitting = true
            this.formError = null

            const res = this.editingVariableId
                ? await this.updateVariable({ id: this.editingVariableId, name })
                : await this.newVariable(name)

            this.submitting = false

            if (!res || !res.body || !res.body.success) {
                this.formError = res && res.body && res.body.error === 'dash.error.custom_variable_name_already_exists'
                    ? this.$t('dash.settings.variables.error-duplicate')
                    : this.$t('dash.settings.variables.error-invalid')
                return
            }

            await this.$store.dispatch('variables/fetchVariables')

            this.cancelForm()
        }
    }
}
</script>

<style lang="scss" scoped>
.settings-variables {
    .sv-header {
        margin-bottom: 16px;

        .sv-title {
            font-weight: 600;
            font-size: 16px;
            margin: 0 0 4px;
        }

        .sv-subtitle {
            color: #9ba3b7;
            font-size: 13px;
            margin: 0;
        }
    }

    .sv-chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-bottom: 10px;
    }

    .sv-chip {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #f5f6fa;
        border-radius: 16px;
        padding: 6px 12px;
        font-size: 13px;

        i {
            cursor: pointer;
            color: #9ba3b7;
            font-size: 11px;

            &:hover {
                color: #6b1f4d;
            }
        }
    }

    .sv-add-trigger {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #6b1f4d;
        font-size: 13px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .sv-create-form {
        display: flex;
        align-items: center;
        gap: 8px;

        input {
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            padding: 6px 10px;
            font-size: 13px;
            outline: none;
        }

        button {
            border: none;
            border-radius: 6px;
            padding: 6px 12px;
            font-size: 13px;
            cursor: pointer;
        }

        .sv-btn-submit {
            background: #6b1f4d;
            color: #fff;

            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .sv-btn-cancel {
            background: #f5f6fa;
            color: #333;
        }
    }

    .sv-error {
        color: #e14e4e;
        font-size: 12px;
        margin: 4px 0 12px;
    }

    .sv-table-wrapper {
        display: flex;
        flex-direction: column;
        border: 1px solid #e3e6ee;
        border-radius: 8px;
        max-width: 100%;
        overflow: hidden;
    }

    .sv-table-toolbar {
        flex-shrink: 0;
        padding: 10px 14px;
        border-bottom: 1px solid #e3e6ee;
        background: #f9fafc;
    }

    .sv-search {
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 260px;
        background: #fff;
        border: 1px solid #e3e6ee;
        border-radius: 6px;
        padding: 6px 10px;

        i {
            color: #9ba3b7;
            font-size: 12px;
        }

        input {
            flex-grow: 1;
            border: none;
            outline: none;
            font-size: 13px;
        }
    }

    .sv-table-scroll {
        overflow: auto;
        max-height: 65vh;
    }

    .sv-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;

        th {
            padding: 10px 14px;
            background: #f9fafc;
            font-weight: 600;
            color: #333;
            font-size: 13px;
            text-align: left;
            white-space: nowrap;
            border-bottom: 1px solid #e3e6ee;
            border-right: 1px solid #eef0f5;
            position: sticky;
            top: 0;
            z-index: 1;

            &:last-child {
                border-right: none;
            }
        }

        .sv-sortable {
            cursor: pointer;
            user-select: none;

            &:hover {
                background: #f1f2f7;

                .sv-bulk-icon {
                    opacity: 1;
                }
            }
        }

        .sv-th-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
        }

        .sv-bulk-icon {
            cursor: pointer;
            font-size: 12px;
            color: #9ba3b7;
            opacity: 0;
            transition: opacity 0.15s;
            padding: 4px;

            &:hover {
                color: #6b1f4d;
            }
        }

        .sv-sort-icon {
            margin-left: 4px;
            font-size: 10px;
            color: #c2c7d4;

            &.fa-sort-up, &.fa-sort-down {
                color: #6b1f4d;
            }
        }

        td {
            border-bottom: 1px solid #eef0f5;
            border-right: 1px solid #eef0f5;
            font-size: 13px;
            padding: 0;
            vertical-align: middle;

            &:last-child {
                border-right: none;
            }
        }

        tbody tr:last-child td {
            border-bottom: none;
        }

        tbody tr:hover {
            background: #fafafc;
        }

        .sv-page-col {
            width: 200px;
        }

        .sv-page-cell {
            display: flex;
            align-items: flex-start;
            gap: 8px;
            padding: 10px 14px;
            color: #333;
            white-space: normal;
            word-break: break-word;
            line-height: 1.35;
        }

        .sv-network-icon {
            flex-shrink: 0;
            width: 16px;
            height: 16px;
            margin-top: 1px;
        }

        .sv-value-input {
            display: block;
            width: 100%;
            height: 100%;
            min-height: 40px;
            min-width: 160px;
            box-sizing: border-box;
            border: none;
            background: transparent;
            border-radius: 0;
            padding: 10px 26px 10px 14px;
            font-size: 13px;
            font-family: inherit;
            color: #333;
            outline: none;

            &::placeholder {
                color: #c2c7d4;
            }

            &:hover {
                background: #f9fafc;
            }

            &:focus {
                background: #fff;
                box-shadow: inset 0 0 0 2px #6b1f4d;
            }
        }
    }

    .sv-empty {
        color: #9ba3b7;
        font-size: 13px;
    }

    .sv-value-cell {
        position: relative;

        &:hover .sv-value-bulk-icon {
            opacity: 1;
        }
    }

    .sv-value-bulk-tooltip {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%);
    }

    .sv-value-bulk-icon {
        padding: 4px;
    }
}
</style>
